import { ThirtyDayStatistics } from '@lune-fe/dapi-models'
import { formatToCurrency } from '@lune-fe/lune-components-lib'
import { formatNumbers, Text, Tooltip } from '@lune-fe/lune-ui-lib'
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined'
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined'
import Box from '@mui/material/Box'
import { FC, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

import WhiteBoxContainerWithContent from 'components/WhiteBoxContainerWithContent'
import useActiveOrganisation from 'hooks/useActiveOrganisation'

const TopStats: FC<{
    stats: ThirtyDayStatistics
}> = ({ stats }) => {
    const navigate = useNavigate()
    const { activeOrg } = useActiveOrganisation()

    const getPercentage = (percentage: number): ReactNode => {
        return percentage > 0 ? (
            <>
                <ArrowUpwardOutlinedIcon /> {percentage}%
            </>
        ) : (
            <>
                <ArrowDownwardOutlinedIcon /> {Math.abs(percentage)}%
            </>
        )
    }

    const statsBoxInfo: {
        label: string
        value: string
        percentage: number
        href: string
        // show green/red percentage chip based on positive/negative value
        // by design we only want to show this for calculations
        // https://www.figma.com/design/h4SBtr4VrIQ0qnkVahwYHI?node-id=2992-87171&m=dev#1481518241
        colorIndicator: boolean
        unit?: string
        isEmpty: boolean
        buttonTooltipLabel: string
    }[] = [
        {
            label: 'Calculations',
            value: `${formatNumbers(stats.numCalculations.amount, 0)}`,
            percentage: Number(formatNumbers(stats.numCalculations.trendIndicator, 2)),
            href: '/calculate-emissions',
            colorIndicator: true,
            isEmpty: !stats.numCalculations.amount,
            buttonTooltipLabel: 'Emissions calculations',
        },
        {
            label: 'Emissions',
            value: `${formatNumbers(parseFloat(stats.quantity.amount) || 0, 2)} tCO₂e`,
            percentage: Number(formatNumbers(stats.quantity.trendIndicator, 2)),
            href: '/analytics',
            colorIndicator: false,
            unit: 'tCO₂e',
            isEmpty: !(parseFloat(stats.quantity.amount) > 0),
            buttonTooltipLabel: 'Analytics',
        },
        {
            label: 'CO₂ purchased',
            value: `${formatNumbers(parseFloat(stats.purchasedAmount.amount) || 0, 2)} t`,
            percentage: Number(formatNumbers(stats.purchasedAmount.trendIndicator, 2)),
            href: '/orders',
            colorIndicator: false,
            unit: 't',
            isEmpty: !(parseFloat(stats.purchasedAmount.amount) > 0),
            buttonTooltipLabel: 'Orders',
        },
        {
            label: 'Offset cost',
            value: activeOrg
                ? `${formatToCurrency(
                      parseFloat(stats.offsetCost.amount) || 0,
                      activeOrg.billingCurrency,
                  )}`
                : '',
            percentage: Number(formatNumbers(stats.offsetCost.trendIndicator, 2)),
            href: '/order',
            colorIndicator: false,
            unit: activeOrg?.billingCurrency,
            isEmpty: !(parseFloat(stats.offsetCost.amount) > 0),
            buttonTooltipLabel: 'Fund climate projects',
        },
    ]

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'stretch',
                justifyContent: 'space-between',
                gap: 2,
            }}
        >
            {statsBoxInfo.map((item, index) => (
                <WhiteBoxContainerWithContent
                    key={`top-stat-${index}`}
                    title={item.label}
                    onClick={() => navigate(item.href)}
                    buttonTooltipLabel={item.buttonTooltipLabel}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        '> div': {
                            width: '100%',
                            alignItems: 'flex-end',
                        },
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-end',
                            gap: 3 / 2,
                        }}
                    >
                        <Text
                            variant={'h5'}
                            sx={{
                                color: item.isEmpty ? `TextTertiary` : `TextPrimary`,
                                minHeight: '32px',
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            {item.value}
                        </Text>
                        {!!item.percentage && (
                            <Tooltip
                                title="vs previous 30 days"
                                placement="top"
                                contentWidth={true}
                            >
                                <Text
                                    variant={'caption'}
                                    sx={{
                                        borderRadius: '20px',
                                        px: 1 / 2,
                                        py: 1 / 4,
                                        backgroundColor: item.colorIndicator
                                            ? item.percentage > 0
                                                ? 'SurfaceInfoGreen100'
                                                : 'SurfaceDestructive100'
                                            : 'Surface300',
                                        color: item.colorIndicator
                                            ? item.percentage > 0
                                                ? 'TextInfoGreen'
                                                : 'TextDestructive'
                                            : 'TextSecondary',
                                        svg: {
                                            height: '12px',
                                            width: '12px',
                                            flex: '1 0 auto',
                                        },
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1 / 4,
                                        mb: '5px',
                                    }}
                                >
                                    {getPercentage(item.percentage)}
                                </Text>
                            </Tooltip>
                        )}
                    </Box>
                </WhiteBoxContainerWithContent>
            ))}
        </Box>
    )
}

export default TopStats
